import { Checkbox, Group, Image, Table, Text, Tooltip } from "@mantine/core";
import { isString } from "lodash";

import lockerIcon from "@/assets/icons/locker.svg";

import type { CheckStatus } from "./components";
import type { ListTableColumn, ListTableColumnFiltered, ListTableHead, ListTableTh } from "./types";

type SelectionProps = {
  checkStatus: CheckStatus;
  onToggleAll: () => void;
  withSelection: boolean;
};

export function renderListTableHead(
  head: ListTableHead,
  checkboxProps?: SelectionProps,
  onOpenUpgrade?: () => void,
  tooltipLabel?: string
) {
  return (
    <Table.Tr>
      {checkboxProps && (
        <Table.Th w={1}>
          {checkboxProps.withSelection && (
            <Checkbox
              checked={checkboxProps.checkStatus === "checked"}
              indeterminate={checkboxProps.checkStatus === "indeterminate"}
              onChange={checkboxProps.onToggleAll}
            />
          )}
        </Table.Th>
      )}
      {head.map((item, idx) => {
        if (isListTableTh(item)) {
          // This will fix the documentpage column header that is currently not working and is read as disabled, we should find a better way to handle this in the future, maybe with a specific property for the disabled state of the column instead of relying on the name of the column
          const maybe = item as any;
          const isNameColumn =
            maybe.id === "name" ||
            (typeof maybe.label === "string" && maybe.label?.toLowerCase() === "name");

          return (
            <Table.Th key={idx} {...item.thProps}>
              <Group gap="sm" wrap="nowrap">
                {item.children || (
                  <Group gap="sm" align="center" wrap="nowrap">
                    <Text
                      size="xs"
                      style={{
                        whiteSpace: "nowrap",
                        color:
                          Boolean((item as any).disabled) && !isNameColumn ? "#999999" : undefined,
                      }}
                    >
                      {item.label}
                    </Text>
                    {item.icon && (
                      <Tooltip label={tooltipLabel ?? "Upgrade to access"} withArrow>
                        <span
                          style={{ display: "flex", cursor: onOpenUpgrade ? "pointer" : undefined }}
                          onClick={onOpenUpgrade}
                        >
                          {item.icon}
                        </span>
                      </Tooltip>
                    )}
                    {Boolean((item as any).disabled) && !isNameColumn && (
                      <Tooltip label={tooltipLabel ?? "Upgrade to access"} withArrow>
                        <Image
                          src={lockerIcon as unknown as string}
                          width={18}
                          height={18}
                          alt={tooltipLabel ?? "locked"}
                          style={{
                            opacity: 0.95,
                            filter: "drop-shadow(0 1px 1px rgba(0,0,0,0.2))",
                            cursor: onOpenUpgrade ? "pointer" : undefined,
                          }}
                          onClick={onOpenUpgrade}
                        />
                      </Tooltip>
                    )}
                    {Boolean((item as any).locked) && !Boolean((item as any).disabled) && (
                      <Tooltip label={tooltipLabel ?? "Upgrade to access"} withArrow>
                        <Image
                          src={lockerIcon as unknown as string}
                          width={18}
                          height={18}
                          alt={tooltipLabel ?? "locked"}
                          style={{
                            opacity: 0.95,
                            filter: "drop-shadow(0 1px 1px rgba(0,0,0,0.2))",
                            cursor: onOpenUpgrade ? "pointer" : undefined,
                          }}
                          onClick={onOpenUpgrade}
                        />
                      </Tooltip>
                    )}
                  </Group>
                )}
                {(item.sorter || item.filter) && !Boolean((item as any).disabled) && (
                  <Group gap="0" wrap="nowrap">
                    {item.sorter}
                    {item.filter}
                  </Group>
                )}
              </Group>
            </Table.Th>
          );
        } else {
          return <Table.Th key={idx}>{item}</Table.Th>;
        }
      })}
    </Table.Tr>
  );
}

export function isListTableTh(value: ListTableTh | string): value is ListTableTh {
  return !isString(value);
}

export function mergeStoredColumns(
  stored: ListTableColumnFiltered[],
  allColumns: ListTableColumnFiltered[]
) {
  const allById = new Map(allColumns.map((col) => [col.id, col]));
  const storedIds = new Set(stored.map((col) => col.id));

  const merged = stored
    .filter((col) => allById.has(col.id))
    .map((col) => {
      const current = allById.get(col.id)!;

      return {
        ...col,
        label: current.label,
        disabled: current.disabled,
      };
    });

  allColumns.forEach((col) => {
    if (!storedIds.has(col.id)) {
      merged.push({
        ...col,
        label: col.label,
        disabled: col.disabled,
        active: true,
      });
    }
  });

  return merged;
}

export function sortAndFilterColumns<T extends ListTableColumn = ListTableColumn>(
  columns: T[],
  filterBy: ListTableColumnFiltered[]
) {
  const columnsMap = new Map(columns.map((col) => [col.id, col]));
  return filterBy
    .filter((f) => f.active)
    .map((f) => columnsMap.get(f.id))
    .filter(Boolean) as T[];
}
